import {
  Box,
  Avatar,
  InputBase,
  IconButton,
  Badge,
  Chip,
  Typography,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Divider,
  TextField,
  Checkbox,
  Button,
  Tooltip,
} from "@mui/material";
import { useState, useEffect, useRef } from "react";
import { Send, Close, AttachFile, Description, Add, Search, Mic, Stop, PlayArrow, Pause, Download, KeyboardArrowUp, DoneAll } from "@mui/icons-material";
import chatimage from "../../../../assets/chatimage.jpeg";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { decryptToken } from "../../../../Utlis/Encryption.js";
import CustomText, { textStyles } from "../../../../components/CustomText";

import { URL } from "../../../../components/BaseUrl.js";
import { updateCount, setUnreadCounts, clearThreadUnreadCount } from "../../../../Redux/Slices/MessageCountSlice";
import MessagingApi from "../../../../services/messagingApi";
import MessagingSocket from "../../../../services/messagingSocket";
import { useNavigate, useSearchParams } from "react-router-dom";
import back from "../../../../assets/back.svg";
import { useLocation } from "react-router-dom";

/** Voice/audio attachments: do not rely on message_type alone — APIs often use "file" or omit "system". */
const isAudioMessagePayload = (msg) => {
  if (!msg?.file_url) return false;
  const ft = String(msg.file_type || "").toLowerCase();
  if (ft.startsWith("audio/")) return true;

  const path = String(msg.file_url || "").split(/[?#]/)[0];
  const fname = String(msg.file_name || "").split(/[?#]/)[0];
  const AUDIO_EXT_RE = /\.(webm|mp3|ogg|wav|m4a|aac|opus|mpeg)(\?|#|$)/i;
  if (AUDIO_EXT_RE.test(path) || AUDIO_EXT_RE.test(fname)) return true;

  const lowerPath = path.toLowerCase();
  if (lowerPath.includes("/audio/") || lowerPath.includes("upload/audio")) return true;

  const c = String(msg.content || "").toLowerCase().trim();
  if (/^audio(\s+message)?$/.test(c)) return true;

  return false;
};

const Messages = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [chatList, setChatList] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [uploadedImageUrl, setUploadedImageUrl] = useState(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const [uploadedFileUrl, setUploadedFileUrl] = useState(null);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  
  // Audio recording states
  const [isRecording, setIsRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [isUploadingAudio, setIsUploadingAudio] = useState(false);
  const [uploadedAudioUrl, setUploadedAudioUrl] = useState(null);
  const [playingAudioId, setPlayingAudioId] = useState(null);
  const [audioProgressById, setAudioProgressById] = useState({});
  
  // Threads/Conversations Pagination states
  const [threadsPage, setThreadsPage] = useState(1);
  const [hasMoreThreads, setHasMoreThreads] = useState(true);
  const [isLoadingMoreThreads, setIsLoadingMoreThreads] = useState(false);
  const [totalThreads, setTotalThreads] = useState(0);
  const THREADS_PER_PAGE = 20;

  // Messages Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [isLoadingMoreMessages, setIsLoadingMoreMessages] = useState(false);
  const [totalMessages, setTotalMessages] = useState(0);
  const MESSAGES_PER_PAGE = 15;
  const isLoadingOlderRef = useRef(false);

  // Mark all as read state
  const [isMarkingAllRead, setIsMarkingAllRead] = useState(false);
  
  // Refs for audio management
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);
  const audioRefs = useRef({});
  const audioBlobUrlsRef = useRef({});
  
  const { totalCount, threads: unreadThreads } = useSelector((state) => state.messageCount);

  const [openNewChatDialog, setOpenNewChatDialog] = useState(false);
  const [newChatStep, setNewChatStep] = useState(1);
  const [selectedRole, setSelectedRole] = useState("");
  const [eligibleUsers, setEligibleUsers] = useState([]);
  const [userSearch, setUserSearch] = useState("");
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [filterRole, setFilterRole] = useState(""); // Filter for sidebar chat list
  const [allChats, setAllChats] = useState([]); // Store all chats before filtering
  const [selectedUsers, setSelectedUsers] = useState([]); // Selected users for multi-chat
  const [isCreatingThreads, setIsCreatingThreads] = useState(false);
  const [bulkMessage, setBulkMessage] = useState(""); // Message to send to multiple users
  const [isSendingBulkMessage, setIsSendingBulkMessage] = useState(false);
  const fromDashboard = location.state?.fromDashboard;
  const rawEncryptedToken = localStorage.getItem("token");
  const token = rawEncryptedToken ? decryptToken(rawEncryptedToken) : null;
  const userId = token ? JSON.parse(atob(token.split(".")[1])).id : null;

  const [messagingApi] = useState(() => new MessagingApi(token));
  const [messagingSocket] = useState(() => new MessagingSocket(token));

  const [socketUserId, setSocketUserId] = useState(null);
  const selectedChatRef = useRef(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    selectedChatRef.current = selectedChat;
  }, [selectedChat]);

  useEffect(() => {
    // Don't auto-scroll when loading older messages
    if (messagesEndRef.current && !isLoadingOlderRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  const formatThreadTime = (dbTimestamp) => {
    try {
      const messageDate = new Date(dbTimestamp);
      if (isNaN(messageDate.getTime())) return "Invalid time";
      const now = new Date();
      const timeDiff = Math.abs(now.getTime() - messageDate.getTime());
      if (timeDiff < 60000) {
        return now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
      }
      return messageDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
    } catch (error) {
      return "Error";
    }
  };

  // Helper function to get role badge label
  // const getRoleBadgeLabel = (role) => {
  //   switch (role) {
  //     case 'account_executive':
  //       return t('Acc. Executive');
  //     case 'company_admin':
  //       return t('Company Admin');
  //     case 'worker':
  //       return t('Employee');
  //     case 'super_admin':
  //     case 'superadmin':
  //       return t('Super Admin');
  //     default:
  //       return t('User');
  //   }
  // };
const getRoleBadgeLabel = (role) => {
  switch (role) {
    case "account_executive":
      return t("roles.accountExecutive");
    case "company_admin":
      return t("roles.companyAdmin");
    case "worker":
      return t("roles.employee");
    case "super_admin":
    case "superadmin":
      return t("roles.superAdmin");
    default:
      return t("roles.user");
  }
};
  // Helper function to get role badge color
  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'account_executive':
        return '#E3F2FD'; // Light blue
      case 'company_admin':
        return '#FFF3E0'; // Light orange
      case 'worker':
        return '#E8F5E9'; // Light green
      case 'super_admin':
      case 'superadmin':
        return '#FCE4EC'; // Light pink
      default:
        return '#F5F5F5'; // Light gray
    }
  };

  // Helper function to get role badge text color
  const getRoleBadgeTextColor = (role) => {
    switch (role) {
      case 'account_executive':
        return '#1565C0'; // Blue
      case 'company_admin':
        return '#E65100'; // Orange
      case 'worker':
        return '#2E7D32'; // Green
      case 'super_admin':
      case 'superadmin':
        return '#C2185B'; // Pink
      default:
        return '#616161'; // Gray
    }
  };

  // Helper function to process thread data
  const processThreadData = (thread) => {
    const formatThreadName = (thread) => {
      return thread.other_user_name || thread.name || 'Unknown User';
    };

    // Format message preview based on type
    let messageToShow = thread.last_message_content || '';
    const messageType = thread.last_message_type;
    
    if (messageType === 'image') {
      messageToShow = messageToShow && messageToShow !== 'File shared' ? `${messageToShow} 📷` : '📷 Image';
    } else if (messageType === 'system' || messageType === 'file') {
      const isAudio = isAudioMessagePayload({
        file_url: thread.last_message_file_url,
        file_type: thread.last_message_file_type,
        file_name: thread.last_message_file_name,
        content: thread.last_message_content,
      });
      if (isAudio) {
        messageToShow = messageToShow && messageToShow !== 'File shared' ? `${messageToShow} 🎵` : '🎵 Audio';
      } else {
        messageToShow = messageToShow && messageToShow !== 'File shared' ? `${messageToShow} 📄` : '📄 Document';
      }
    }

    const timeToShow = formatThreadTime(thread.last_message_at || thread.created_at);

    return {
      id: thread.id,
      name: formatThreadName(thread),
      msg: messageToShow,
      time: timeToShow,
      icon: thread.other_user_avatar || '',
      other_user_id: thread.other_user_id,
      other_user_role: thread.other_user_role,
      other_user_email: thread.other_user_email || thread.email || '',
      created_at: thread.created_at,
      last_message_at: thread.last_message_at,
      lastMessageAt: thread.last_message_at,
      other_user_is_online: thread.other_user_is_online,
      other_user_last_seen_at: thread.other_user_last_seen_at,
      unreadCount: thread.unread_count || 0,
      role: thread.other_user_role,
    };
  };

  useEffect(() => {
    if (!token) return;
    messagingSocket.connect();

    const unsubscribeAuth = messagingSocket.on("authenticated", (data) => {
      if (data.success && data.user) {
        setSocketUserId(data.user.id);
      }
    });

    const unsubscribeMessage = messagingSocket.on("receive_message", (data) => {
      const newMsg = data.message || data;
      const threadId = data.threadId || data.thread_id;

      if (selectedChatRef.current && String(threadId) === String(selectedChatRef.current.id)) {
        setMessages((prev) => {
          const isDuplicate = prev.some(msg => msg.id === newMsg.id || (msg.tempId && msg.tempId === newMsg.tempId));
          if (isDuplicate) return prev;
          // Add new message and sort by created_at to maintain chronological order
          const updated = [...prev, newMsg];
          return updated.sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0));
        });
        messagingApi.markAsRead(threadId);
        messagingSocket.markAsRead(threadId);
      } else {
        dispatch(updateCount({ type: 'increment' }));
      }

      setAllChats((prev) => {
        // Format message preview based on type
        let displayMsg = newMsg.content;
        
        if (newMsg.message_type === "image") {
          if (newMsg.content && newMsg.content !== "File shared") {
            displayMsg = `${newMsg.content} 📷`;
          } else {
            displayMsg = "📷 Image";
          }
        } else if (isAudioMessagePayload(newMsg)) {
          const contentNorm = String(newMsg.content || "").toLowerCase().trim();
          if (newMsg.content && newMsg.content !== "File shared" && newMsg.content !== "Audio" && contentNorm !== "audio message") {
            displayMsg = `${newMsg.content} 🎵`;
          } else {
            displayMsg = "🎵 Audio";
          }
        } else if (newMsg.message_type === "system") {
          if (newMsg.content && newMsg.content !== "File shared") {
            displayMsg = `${newMsg.content} 📄`;
          } else {
            displayMsg = "📄 Document";
          }
        }

        const updated = prev.map((c) =>
          String(c.id) === String(threadId)
            ? { ...c, msg: displayMsg, time: formatThreadTime(newMsg.created_at), unreadCount: String(threadId) !== String(selectedChatRef.current?.id) ? (c.unreadCount || 0) + 1 : 0, lastMessageAt: newMsg.created_at }
            : c
        );
        const sorted = updated.sort((a, b) => new Date(b.lastMessageAt || b.created_at || 0) - new Date(a.lastMessageAt || a.created_at || 0));

        // Apply filter to chat list based on updated allChats
        if (filterRole) {
          setChatList(sorted.filter(chat => chat.role === filterRole));
        } else {
          setChatList(sorted);
        }

        return sorted;
      });
    });

    const unsubscribeSent = messagingSocket.on("message_sent", (data) => {
      if (data.success && data.message) {
        const message = data.message;
        const threadId = data.threadId || message.thread_id;

        if (selectedChatRef.current && String(threadId) === String(selectedChatRef.current.id)) {
          setMessages((prev) => {
            // Check if message already exists (from optimistic update)
            const existingIndex = prev.findIndex(msg => msg.tempId === data.tempId || msg.id === message.id);
            if (existingIndex >= 0) {
              const optimisticMsg = prev[existingIndex];
              if (optimisticMsg?.tempId && message?.id) {
                setAudioProgressById((prevProgress) => {
                  const tempProgress = prevProgress[optimisticMsg.tempId];
                  if (!tempProgress) return prevProgress;
                  return {
                    ...prevProgress,
                    [message.id]: tempProgress,
                  };
                });
              }
              // Replace optimistic message with real one
              const updated = [...prev];
              updated[existingIndex] = message;
              // Sort by created_at to maintain chronological order
              return updated.sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0));
            }
            // Add new message if it doesn't exist
            const isDuplicate = prev.some(msg => msg.id === message.id);
            if (isDuplicate) return prev;
            // Add and sort by created_at
            const updated = [...prev, message];
            return updated.sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0));
          });
        }

        // Update chat list
        setAllChats((prev) => {
          // Format message preview based on type
          let displayMsg = message.content || message.file_name || "File";
          
          if (message.message_type === "image") {
            if (message.content && message.content !== "File shared") {
              displayMsg = `${message.content} 📷`;
            } else {
              displayMsg = "📷 Image";
            }
          } else if (isAudioMessagePayload(message)) {
            const contentNorm = String(message.content || "").toLowerCase().trim();
            if (message.content && message.content !== "File shared" && message.content !== "Audio" && contentNorm !== "audio message") {
              displayMsg = `${message.content} 🎵`;
            } else {
              displayMsg = "🎵 Audio";
            }
          } else if (message.message_type === "system") {
            if (message.content && message.content !== "File shared") {
              displayMsg = `${message.content} 📄`;
            } else {
              displayMsg = "📄 Document";
            }
          }

          const updated = prev.map((c) =>
            String(c.id) === String(threadId)
              ? { ...c, msg: displayMsg, time: formatThreadTime(message.created_at), lastMessageAt: message.created_at }
              : c
          );
          const sorted = updated.sort((a, b) => new Date(b.lastMessageAt || b.created_at || 0) - new Date(a.lastMessageAt || a.created_at || 0));

          if (filterRole) {
            setChatList(sorted.filter(chat => chat.role === filterRole));
          } else {
            setChatList(sorted);
          }

          return sorted;
        });
      }
    });

    return () => {
      unsubscribeAuth();
      unsubscribeMessage();
      unsubscribeSent();
      messagingSocket.disconnect();
    };
  }, [token, messagingSocket, dispatch, messagingApi, filterRole]);

  useEffect(() => {
    if (selectedChat?.id) {
      messagingSocket.joinThreadRoom(selectedChat.id);
      return () => messagingSocket.leaveThreadRoom(selectedChat.id);
    }
  }, [selectedChat?.id, messagingSocket]);

  const fetchThreads = async (page = 1, resetList = true) => {
    const response = await messagingApi.getThreads(page, THREADS_PER_PAGE);
    if (!response.error && Array.isArray(response.data?.threads)) {
      const processedThreads = response.data.threads.map(processThreadData);
      
      // Get pagination info
      const pagination = response.data.pagination;
      const total = pagination?.total_count || pagination?.total || 0;
      setTotalThreads(total);
      
      // Check if there are more threads
      const hasMore = pagination?.has_next_page !== undefined 
        ? pagination.has_next_page 
        : (total > 0 ? processedThreads.length + (page - 1) * THREADS_PER_PAGE < total : processedThreads.length >= THREADS_PER_PAGE);
      setHasMoreThreads(hasMore);
      setThreadsPage(page);

      if (resetList) {
        // Apply filter if active
        if (filterRole) {
          const filtered = processedThreads.filter(thread => thread.role === filterRole);
          
          // If current selected chat doesn't match filter, clear it
          if (selectedChat && selectedChat.role !== filterRole) {
            setAllChats(processedThreads);
            setChatList(filtered);
            setSelectedChat(null);
            setMessages([]);
          } else if (filtered.length > 0 && !selectedChat) {
            // Auto-select first chat - clear its unread count before setting state
            const firstChat = filtered[0];
            const firstChatId = firstChat.id;
            
            // Update unread count to 0 for the first chat in both lists
            const updatedAllChats = processedThreads.map(c => 
              c.id === firstChatId ? { ...c, unreadCount: 0 } : c
            );
            const updatedFiltered = filtered.map(c => 
              c.id === firstChatId ? { ...c, unreadCount: 0 } : c
            );
            
            setAllChats(updatedAllChats);
            setChatList(updatedFiltered);
            setSelectedChat({ ...firstChat, unreadCount: 0 });
            fetchMessages(firstChat.id);
            messagingApi.markAsRead(firstChat.id);
            messagingSocket.markAsRead(firstChat.id);
            dispatch(clearThreadUnreadCount({ threadId: firstChat.id }));
          } else {
            setAllChats(processedThreads);
            setChatList(filtered);
          }
        } else {
          if (processedThreads.length > 0 && !selectedChat) {
            // Auto-select first chat - clear its unread count before setting state
            const firstChat = processedThreads[0];
            const firstChatId = firstChat.id;
            
            // Update unread count to 0 for the first chat
            const updatedThreads = processedThreads.map(c => 
              c.id === firstChatId ? { ...c, unreadCount: 0 } : c
            );
            
            setAllChats(updatedThreads);
            setChatList(updatedThreads);
            setSelectedChat({ ...firstChat, unreadCount: 0 });
            fetchMessages(firstChat.id);
            messagingApi.markAsRead(firstChat.id);
            messagingSocket.markAsRead(firstChat.id);
            dispatch(clearThreadUnreadCount({ threadId: firstChat.id }));
          } else {
            setAllChats(processedThreads);
            setChatList(processedThreads);
          }
        }
      } else {
        // Append to existing list (for load more)
        setAllChats(prev => {
          const existingIds = new Set(prev.map(c => c.id));
          const newThreads = processedThreads.filter(t => !existingIds.has(t.id));
          return [...prev, ...newThreads];
        });
        setChatList(prev => {
          const existingIds = new Set(prev.map(c => c.id));
          const filtered = filterRole 
            ? processedThreads.filter(t => t.role === filterRole && !existingIds.has(t.id))
            : processedThreads.filter(t => !existingIds.has(t.id));
          return [...prev, ...filtered];
        });
      }

      console.log("📊 Threads loaded:", processedThreads.length, "| Total:", total, "| Has more:", hasMore);
    }
  };

  // Load more threads
  const loadMoreThreads = async () => {
    if (isLoadingMoreThreads || !hasMoreThreads) return;
    
    setIsLoadingMoreThreads(true);
    try {
      const nextPage = threadsPage + 1;
      await fetchThreads(nextPage, false);
    } catch (error) {
      console.error("Error loading more threads:", error);
    } finally {
      setIsLoadingMoreThreads(false);
    }
  };

  useEffect(() => {
    if (token) fetchThreads();
  }, [token]);

  // Handle URL navigation - open specific user conversation
  useEffect(() => {
    const userIdFromUrl = searchParams.get('userId');
    const roleFromUrl = searchParams.get('role');

    if (!userIdFromUrl || !token || chatList.length === 0) return;

    const userIdNum = parseInt(userIdFromUrl, 10);
    if (isNaN(userIdNum)) return;

    console.log("📬 URL navigation: userId=", userIdNum, "role=", roleFromUrl);

    // Find existing thread with this user
    const existingThread = chatList.find(chat => {
      const chatUserId = typeof chat.other_user_id === 'string' 
        ? parseInt(chat.other_user_id, 10) 
        : chat.other_user_id;
      return chatUserId === userIdNum;
    });

    if (existingThread) {
      // Move thread to top and select it
      setChatList(prev => {
        const filtered = prev.filter(c => c.id !== existingThread.id);
        return [existingThread, ...filtered];
      });
      setAllChats(prev => {
        const filtered = prev.filter(c => c.id !== existingThread.id);
        return [existingThread, ...filtered];
      });
      setSelectedChat(existingThread);
      setCurrentPage(1);
      setHasMoreMessages(true);
      fetchMessages(existingThread.id, 1, true);
      messagingApi.markAsRead(existingThread.id);
      messagingSocket.markAsRead(existingThread.id);
      dispatch(clearThreadUnreadCount({ threadId: existingThread.id }));
      setChatList(prev => prev.map(c => c.id === existingThread.id ? { ...c, unreadCount: 0 } : c));
      setAllChats(prev => prev.map(c => c.id === existingThread.id ? { ...c, unreadCount: 0 } : c));
      // Clear URL params
      setSearchParams({});
      console.log("📬 Found existing thread, selected:", existingThread.id);
      return;
    }

    // No existing thread, create new one
    const createNewThread = async () => {
      try {
        const response = await messagingApi.createThread(userIdNum, roleFromUrl || 'worker');
        if (!response.error && response.data?.thread) {
          const thread = response.data.thread;
          const threadUserId = thread.other_user_id 
            ? (typeof thread.other_user_id === 'string' ? parseInt(thread.other_user_id, 10) : thread.other_user_id) 
            : userIdNum;

          const confirmedThread = {
            id: thread.id,
            name: thread.other_user_name || 'User',
            msg: '',
            time: formatThreadTime(new Date().toISOString()),
            icon: thread.other_user_avatar || '',
            other_user_id: threadUserId,
            other_user_role: thread.other_user_role || roleFromUrl,
            other_user_email: thread.other_user_email || '',
            created_at: thread.created_at,
            last_message_at: thread.last_message_at,
            lastMessageAt: thread.last_message_at,
            unreadCount: 0,
            role: thread.other_user_role || roleFromUrl,
          };

          // Add to top of list
          setChatList(prev => {
            const filtered = prev.filter(c => c.id !== confirmedThread.id);
            return [confirmedThread, ...filtered];
          });
          setAllChats(prev => {
            const filtered = prev.filter(c => c.id !== confirmedThread.id);
            return [confirmedThread, ...filtered];
          });
          setSelectedChat(confirmedThread);
          setCurrentPage(1);
          setHasMoreMessages(true);
          fetchMessages(confirmedThread.id, 1, true);
          // Clear URL params
          setSearchParams({});
          console.log("📬 Created new thread:", confirmedThread.id);
        }
      } catch (error) {
        console.error("Error creating thread from URL:", error);
      }
    };

    createNewThread();
  }, [searchParams, chatList.length, token]);

  // Filter chat list when filterRole changes
  useEffect(() => {
    if (filterRole) {
      const filtered = allChats.filter(chat => chat.role === filterRole);
      
      // If current selected chat doesn't match filter, clear it
      if (selectedChat && selectedChat.role !== filterRole) {
        setChatList(filtered);
        setSelectedChat(null);
        setMessages([]);
      } else if (filtered.length > 0 && !selectedChat) {
        // Auto-select first filtered chat - clear its unread count
        const firstFiltered = filtered[0];
        const firstChatId = firstFiltered.id;
        
        // Update unread count to 0 for the first chat
        const updatedFiltered = filtered.map(c => 
          c.id === firstChatId ? { ...c, unreadCount: 0 } : c
        );
        const updatedAllChats = allChats.map(c => 
          c.id === firstChatId ? { ...c, unreadCount: 0 } : c
        );
        
        setChatList(updatedFiltered);
        setAllChats(updatedAllChats);
        setSelectedChat({ ...firstFiltered, unreadCount: 0 });
        fetchMessages(firstFiltered.id);
        messagingApi.markAsRead(firstFiltered.id);
        messagingSocket.markAsRead(firstFiltered.id);
        dispatch(clearThreadUnreadCount({ threadId: firstFiltered.id }));
      } else {
        setChatList(filtered);
      }
    } else {
      setChatList(allChats);
    }
  }, [filterRole, allChats, messagingApi, messagingSocket, dispatch]);

  const fetchMessages = async (threadId, page = 1, resetList = true) => {
    const response = await messagingApi.getThreadMessages(threadId, page, MESSAGES_PER_PAGE);
    if (!response.error && response.data?.messages) {
      // Get pagination info
      const pagination = response.data.pagination;
      const total = pagination?.total_count || pagination?.total || 0;
      setTotalMessages(total);
      
      // Check if there are more messages
      const loadedCount = response.data.messages.length + (page - 1) * MESSAGES_PER_PAGE;
      const hasMore = pagination?.has_next_page !== undefined 
        ? pagination.has_next_page 
        : (total > 0 ? loadedCount < total : response.data.messages.length >= MESSAGES_PER_PAGE);
      setHasMoreMessages(hasMore);
      setCurrentPage(page);

      // Reverse to get oldest first, then sort by created_at to ensure proper order
      const sortedMessages = response.data.messages.reverse().sort((a, b) =>
        new Date(a.created_at || 0) - new Date(b.created_at || 0)
      );

      if (resetList) {
        setMessages(sortedMessages);
      } else {
        // Prepend older messages (for load more)
        setMessages(prev => {
          const existingIds = new Set(prev.map(m => m.id));
          const newMessages = sortedMessages.filter(m => !existingIds.has(m.id));
          const combined = [...newMessages, ...prev];
          return combined.sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0));
        });
      }

      console.log("📊 Messages loaded:", sortedMessages.length, "| Total:", total, "| Has more:", hasMore, "| has_next_page:", pagination?.has_next_page);
    } else {
      if (resetList) {
        setMessages([]);
        setHasMoreMessages(false);
      }
    }
  };

  // Load more (older) messages
  const loadMoreMessages = async () => {
    if (isLoadingMoreMessages || !hasMoreMessages || !selectedChat?.id) return;
    
    isLoadingOlderRef.current = true;
    setIsLoadingMoreMessages(true);
    
    try {
      const nextPage = currentPage + 1;
      await fetchMessages(selectedChat.id, nextPage, false);
    } catch (error) {
      console.error("Error loading more messages:", error);
    } finally {
      setIsLoadingMoreMessages(false);
      // Keep isLoadingOlderRef true for a bit to prevent auto-scroll
      setTimeout(() => {
        isLoadingOlderRef.current = false;
      }, 100);
    }
  };

  const handleChatClick = (chat) => {
    setSelectedChat(chat);
    // Reset pagination when switching chats
    setCurrentPage(1);
    setHasMoreMessages(true);
    fetchMessages(chat.id, 1, true);
    messagingApi.markAsRead(chat.id);
    messagingSocket.markAsRead(chat.id);
    dispatch(clearThreadUnreadCount({ threadId: chat.id }));
    setAllChats(prev => prev.map(c => c.id === chat.id ? { ...c, unreadCount: 0 } : c));
    setChatList(prev => prev.map(c => c.id === chat.id ? { ...c, unreadCount: 0 } : c));
  };

  // Mark all messages as read
  const markAllMessagesAsRead = async () => {
    if (isMarkingAllRead || totalCount === 0) return;
    
    setIsMarkingAllRead(true);
    try {
      // Call API to mark all as read
      const response = await fetch(`${URL}/api/messages/mark-all-read`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        // Emit socket event for real-time sync
        if (messagingSocket.socket && messagingSocket.socket.connected) {
          messagingSocket.emit("mark_all_messages_read", {});
        }

        // Update Redux state
        dispatch(setUnreadCounts({
          total: 0,
          threads: {},
          roleBased: { account_executive: 0, company_admin: 0, worker: 0, super_admin: 0 }
        }));

        // Clear unread indicators in chat list
        setChatList(prev => prev.map(chat => ({ ...chat, unreadCount: 0 })));
        setAllChats(prev => prev.map(chat => ({ ...chat, unreadCount: 0 })));

        console.log("✅ All messages marked as read");
      } else {
        console.error("Failed to mark all messages as read:", response.status);
      }
    } catch (error) {
      console.error("Error marking all messages as read:", error);
    } finally {
      setIsMarkingAllRead(false);
    }
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    console.log("[Inbox][Upload] File selected:", {
      name: file.name,
      type: file.type,
      size: file.size,
      selectedChatId: selectedChat?.id || null
    });

    const isImage = file.type.startsWith('image/');
    const isDocument = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/plain',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation'
    ].includes(file.type);

    // Validate file type
    if (!isImage && !isDocument) {
      console.warn("[Inbox][Upload] Rejected file type:", file.type);
      alert("Please select an image, PDF, or Word document");
      return;
    }

    // Validate file size (5MB for images, 10MB for documents)
    const maxSize = isImage ? 5 * 1024 * 1024 : 10 * 1024 * 1024;
    if (file.size > maxSize) {
      console.warn("[Inbox][Upload] Rejected file size:", {
        size: file.size,
        maxSize,
        kind: isImage ? "image" : "document"
      });
      alert(isImage ? "Image size should be less than 5MB" : "File size should be less than 10MB");
      return;
    }

    if (isImage) {
      // Handle image upload
      setSelectedImageFile(file);

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);

      // Auto-upload image
      setIsUploadingImage(true);
      try {
        console.log("[Inbox][Upload][Image] Starting upload", {
          endpoint: `${URL}/api/upload/image`,
          hasToken: Boolean(token),
          fileName: file.name,
          fileType: file.type,
          fileSize: file.size
        });
        const formData = new FormData();
        formData.append("image", file);
        const res = await fetch(`${URL}/api/upload/image`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });
        console.log("[Inbox][Upload][Image] Response received", {
          ok: res.ok,
          status: res.status,
          statusText: res.statusText
        });
        const data = await res.json();
        console.log("[Inbox][Upload][Image] Response payload:", data);
        if (!data.error && data.data?.url) {
          setUploadedImageUrl(data.data.url);
          console.log("✅ Image uploaded successfully:", data.data.url);
        } else {
          console.error("[Inbox][Upload][Image] Upload response indicates failure", {
            message: data?.message,
            error: data?.error,
            data: data?.data
          });
          throw new Error(data.message || "Image upload failed");
        }
      } catch (error) {
        console.error("❌ Image upload failed:", error);
        console.error("[Inbox][Upload][Image] Detailed error context:", {
          message: error?.message,
          stack: error?.stack,
          fileName: file?.name,
          fileType: file?.type,
          fileSize: file?.size
        });
        alert("Image upload failed. Please try again.");
        setImagePreview(null);
        setSelectedImageFile(null);
      } finally {
        setIsUploadingImage(false);
        event.target.value = ''; // Reset input
      }
    } else {
      // Handle document upload
      setSelectedFile(file);
      setIsUploadingFile(true);
      try {
        const formData = new FormData();
        formData.append("pdf", file);
        const res = await fetch(`${URL}/api/upload/pdf`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });
        const data = await res.json();
        if (!data.error && (data.data?.url || data.url)) {
          setUploadedFileUrl(data.data?.url || data.url);
          console.log("✅ File uploaded successfully:", data.data?.url || data.url);
        } else {
          throw new Error(data.message || "File upload failed");
        }
      } catch (error) {
        console.error("❌ File upload failed:", error);
        alert("File upload failed. Please try again.");
        setSelectedFile(null);
      } finally {
        setIsUploadingFile(false);
        event.target.value = ''; // Reset input
      }
    }
  };

  const clearUploadedImage = () => {
    setImagePreview(null);
    setSelectedImageFile(null);
    setUploadedImageUrl(null);
  };

  const clearUploadedFile = () => {
    setSelectedFile(null);
    setUploadedFileUrl(null);
  };

  const clearUploadedAudio = () => {
    setAudioBlob(null);
    setAudioUrl(null);
    if (audioUrl) {
      window.URL.revokeObjectURL(audioUrl);
    }
    setUploadedAudioUrl(null);
  };

  // Format duration helper
  const formatDuration = (seconds) => {
    if (!seconds || !isFinite(seconds)) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // Audio upload function
  const uploadAudio = async (audioBlob) => {
    const formData = new FormData();
    formData.append("audio", audioBlob, "audio.webm");
    
    const response = await fetch(`${URL}/api/upload/audio`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    });
    
    const result = await response.json();
    
    if (!result.error && result.data?.url) {
      return result.data.url;
    } else if (!result.error && result.url) {
      return result.url;
    } else {
      throw new Error(result.message || "Audio upload failed");
    }
  };

  // Start recording audio
  const startRecording = async () => {
    try {
      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      // Collect audio chunks
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      // Handle recording stop
      mediaRecorder.onstop = async () => {
        // Create blob from chunks
        const audioBlob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        const url = window.URL.createObjectURL(audioBlob);
        setAudioBlob(audioBlob);
        setAudioUrl(url);
        
        // Auto-upload audio
        setIsUploadingAudio(true);
        try {
          const audioFileUrl = await uploadAudio(audioBlob);
          setUploadedAudioUrl(audioFileUrl);
          console.log("✅ Audio uploaded successfully:", audioFileUrl);
        } catch (error) {
          console.error("❌ Audio upload failed:", error);
          alert("Audio upload failed. Please try again.");
          clearUploadedAudio();
        } finally {
          setIsUploadingAudio(false);
        }
      };

      // Start recording
      mediaRecorder.start();
      setIsRecording(true);
      setRecordingDuration(0);

      // Start timer
      recordingTimerRef.current = setInterval(() => {
        setRecordingDuration((prev) => prev + 1);
      }, 1000);
    } catch (error) {
      console.error("❌ Error starting recording:", error);
      alert("Microphone access denied. Please allow microphone access and try again.");
    }
  };

  // Stop recording audio
  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current.stream.getTracks().forEach((track) => track.stop());
      setIsRecording(false);
      
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }
    }
  };

  // Toggle audio playback
  const toggleAudioPlayback = async (messageId, audioUrl) => {
    if (!audioUrl || audioUrl.trim() === '') {
      console.error("❌ Invalid audio URL:", audioUrl);
      return;
    }

    // If already playing this audio, pause it
    if (playingAudioId === messageId) {
      const audio = audioRefs.current[messageId];
      if (audio) {
        audio.pause();
        setPlayingAudioId(null);
        return;
      }
    }

    try {
      // Construct full URL if needed
      const fullAudioUrl = audioUrl.startsWith('http://') || audioUrl.startsWith('https://')
        ? audioUrl
        : `${URL}${audioUrl}`;

      // Fetch audio with authentication (handles CORS)
      const response = await fetch(fullAudioUrl, {
        method: "GET",
        headers: { 
          Authorization: `Bearer ${token}`,
          'Accept': 'audio/webm, audio/*'
        },
        mode: 'cors'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      
      // Store blob URL for cleanup
      audioBlobUrlsRef.current[messageId] = blobUrl;

      // Create audio element
      const audio = new Audio(blobUrl);
      audioRefs.current[messageId] = audio;

      // Load metadata (duration)
      audio.onloadedmetadata = () => {
        const duration = isFinite(audio.duration) ? Math.floor(audio.duration) : 0;
        setAudioProgressById(prev => ({
          ...prev,
          [messageId]: { current: 0, duration }
        }));
      };

      // Track progress
      audio.ontimeupdate = () => {
        setAudioProgressById(prev => ({
          ...prev,
          [messageId]: {
            current: Math.floor(audio.currentTime || 0),
            duration: Math.floor(audio.duration || prev[messageId]?.duration || 0)
          }
        }));
      };

      // Handle end
      audio.onended = () => {
        setPlayingAudioId(null);
        // Cleanup blob URL
        if (audioBlobUrlsRef.current[messageId]) {
          window.URL.revokeObjectURL(audioBlobUrlsRef.current[messageId]);
          delete audioBlobUrlsRef.current[messageId];
        }
        audioRefs.current[messageId] = null;
        setAudioProgressById(prev => ({ 
          ...prev, 
          [messageId]: { current: 0, duration: prev[messageId]?.duration || 0 } 
        }));
      };

      // Handle errors
      audio.onerror = (e) => {
        setPlayingAudioId(null);
        console.error("❌ Error playing audio:", e);
        alert("Error playing audio. The audio file may be corrupted or unsupported.");
        // Cleanup on error
        if (audioBlobUrlsRef.current[messageId]) {
          window.URL.revokeObjectURL(audioBlobUrlsRef.current[messageId]);
          delete audioBlobUrlsRef.current[messageId];
        }
        audioRefs.current[messageId] = null;
      };

      // Play audio
      await audio.play();
      setPlayingAudioId(messageId);
      
    } catch (error) {
      console.error("❌ Error fetching/playing audio:", error);
      alert("Failed to load audio file. Please check the URL or try again.");
    }
  };

  // Preload audio duration for received/incoming audio messages so the
  // bubble shows the real length before the user hits play.
  // We probe metadata via an authenticated fetch (same as toggleAudioPlayback),
  // write only `duration` into audioProgressById, then release the blob URL.
  const preloadedAudioMetaRef = useRef(new Set());
  const audioProgressByIdRef = useRef(audioProgressById);
  useEffect(() => {
    audioProgressByIdRef.current = audioProgressById;
  }, [audioProgressById]);

  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;

    const preloadOne = async (msg) => {
      const messageId = msg.id;
      if (messageId === undefined || messageId === null) return;
      if (preloadedAudioMetaRef.current.has(messageId)) return;
      if ((audioProgressByIdRef.current?.[messageId]?.duration || 0) > 0) {
        preloadedAudioMetaRef.current.add(messageId);
        return;
      }
      const rawUrl = msg.file_url || "";
      if (!rawUrl) return;

      preloadedAudioMetaRef.current.add(messageId);

      const fullAudioUrl =
        rawUrl.startsWith("http://") || rawUrl.startsWith("https://")
          ? rawUrl
          : `${URL}${rawUrl}`;

      let blobUrl = null;
      let tempAudio = null;
      const cleanup = () => {
        if (tempAudio) {
          tempAudio.onloadedmetadata = null;
          tempAudio.ontimeupdate = null;
          tempAudio.onerror = null;
          try {
            tempAudio.pause();
          } catch (_) {}
          tempAudio.src = "";
          tempAudio = null;
        }
        if (blobUrl) {
          window.URL.revokeObjectURL(blobUrl);
          blobUrl = null;
        }
      };

      try {
        const response = await fetch(fullAudioUrl, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "audio/webm, audio/*",
          },
          mode: "cors",
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const blob = await response.blob();
        if (cancelled) return;

        blobUrl = window.URL.createObjectURL(blob);
        tempAudio = new Audio();
        tempAudio.preload = "metadata";

        const commitDuration = (rawDuration) => {
          const duration = isFinite(rawDuration) ? Math.floor(rawDuration) : 0;
          if (!cancelled && duration > 0) {
            setAudioProgressById((prev) => {
              const cur = prev[messageId];
              if (cur && cur.duration === duration) return prev;
              return {
                ...prev,
                [messageId]: { current: cur?.current || 0, duration },
              };
            });
          }
          cleanup();
        };

        // WebM files produced by MediaRecorder often report duration as
        // Infinity until the media is seeked to the end. Force-seek to
        // resolve the real duration, then reset and clean up.
        tempAudio.onloadedmetadata = () => {
          const d = tempAudio.duration;
          if (!isFinite(d) || isNaN(d) || d === 0) {
            const onTimeUpdate = () => {
              tempAudio.ontimeupdate = null;
              try {
                tempAudio.currentTime = 0;
              } catch (_) {}
              commitDuration(tempAudio.duration);
            };
            tempAudio.ontimeupdate = onTimeUpdate;
            try {
              tempAudio.currentTime = 1e9;
            } catch (_) {
              commitDuration(tempAudio.duration);
            }
          } else {
            commitDuration(d);
          }
        };
        tempAudio.onerror = () => {
          cleanup();
          preloadedAudioMetaRef.current.delete(messageId);
        };
        tempAudio.src = blobUrl;
      } catch (err) {
        cleanup();
        preloadedAudioMetaRef.current.delete(messageId);
      }
    };

    messages.forEach((m) => {
      if (isAudioMessagePayload(m)) preloadOne(m);
    });

    return () => {
      cancelled = true;
    };
  }, [messages, token]);

  // Cleanup audio refs on unmount
  useEffect(() => {
    return () => {
      // Cleanup all audio blob URLs
      Object.values(audioBlobUrlsRef.current).forEach(url => {
        window.URL.revokeObjectURL(url);
      });
      // Stop all audio elements
      Object.values(audioRefs.current).forEach(audio => {
        if (audio) {
          audio.pause();
          audio.src = '';
        }
      });
      // Stop recording if active
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        mediaRecorderRef.current.stop();
        mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
      }
      // Clear timer
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
    };
  }, []);

  const handleSendMessage = async () => {
    if (!selectedChat?.id) return;
    if (!newMessage.trim() && !uploadedImageUrl && !uploadedFileUrl && !uploadedAudioUrl) return;

    const messageContent = newMessage.trim();
    const tempId = `temp-${Date.now()}-${Math.random()}`;
    const currentUserId = socketUserId || userId;

    // Determine message type and content
    const isImage = uploadedImageUrl || imagePreview;
    const isFile = uploadedFileUrl || selectedFile;
    const isAudio = uploadedAudioUrl || audioBlob;
    
    let messageType = "text";
    let fileUrl = null;
    let fileName = null;
    let fileType = null;

    if (isAudio) {
      messageType = "system";
      fileUrl = uploadedAudioUrl;
      fileName = "audio.webm";
      fileType = "audio/webm";
    } else if (isImage) {
      messageType = "image";
      fileUrl = uploadedImageUrl || imagePreview;
    } else if (isFile) {
      messageType = "system";
      fileUrl = uploadedFileUrl;
      fileName = selectedFile?.name || null;
      fileType = selectedFile?.type || null;
    }

    const displayContent = messageContent || 
      (isImage ? "Image" : isAudio ? "Audio" : isFile ? (fileName || "File") : "");

    // Keep recorded duration visible before playback metadata is loaded.
    if (isAudio) {
      setAudioProgressById((prev) => ({
        ...prev,
        [tempId]: {
          current: 0,
          duration: Math.max(0, Math.floor(recordingDuration || 0)),
        },
      }));
    }

    // Create optimistic message
    const optimisticMessage = {
      id: null,
      tempId: tempId,
      thread_id: selectedChat.id,
      sender_id: Number(currentUserId) || currentUserId,
      sender_name: "You",
      sender_role: "worker",
      content: displayContent,
      message_type: messageType,
      file_url: fileUrl,
      file_name: fileName,
      file_type: fileType,
      is_read: false,
      created_at: new Date().toISOString()
    };

    // Add optimistic message immediately to UI and sort by timestamp
    setMessages((prev) => {
      const updated = [...prev, optimisticMessage];
      return updated.sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0));
    });

    // Clear input
    const messageToSend = messageContent;
    setNewMessage("");

    try {
      // Try to send via socket first (sendMessage checks connection internally)
      const messageData = {
        threadId: selectedChat.id,
        content: messageToSend || (isAudio ? "Audio" : isImage ? "Image" : isFile ? (fileName || "File") : ""),
        messageType: messageType,
        tempId: tempId,
        ...(fileUrl && { fileUrl, fileName, fileType })
      };
      
      // Check if socket is available and connected before trying to send
      const socket = messagingSocket.socket;
      if (socket && socket.connected) {
        messagingSocket.sendMessage(messageData);
      } else {
        // Fallback to API if socket is not connected
        if (uploadedAudioUrl && audioBlob) {
          await messagingApi.sendMessageWithFile(selectedChat.id, audioBlob, messageToSend || "Audio", "system");
          clearUploadedAudio();
        } else if (uploadedImageUrl && selectedImageFile) {
          await messagingApi.sendMessageWithFile(selectedChat.id, selectedImageFile, messageToSend || "", "image");
          clearUploadedImage();
        } else if (uploadedFileUrl && selectedFile) {
          const fileType = selectedFile.type === "application/pdf" ? "pdf" : "document";
          await messagingApi.sendMessageWithFile(selectedChat.id, selectedFile, messageToSend || "", fileType);
          clearUploadedFile();
        } else {
          await messagingApi.sendMessage(selectedChat.id, messageToSend);
        }
      }

      // Clear uploaded files/audio
      if (isAudio) {
        clearUploadedAudio();
      } else if (isImage) {
        clearUploadedImage();
      } else if (isFile) {
        clearUploadedFile();
      }

      // Don't call fetchMessages here - let socket events handle message updates
      fetchThreads();
    } catch (error) {
      console.error("Error sending message:", error);
      // Remove optimistic message on error
      setMessages((prev) => prev.filter(msg => msg.tempId !== tempId));
    }
  };

  const handleRoleSelect = (role) => {
    setSelectedRole(role);
    setUserSearch(""); // Clear search when selecting a role
    setNewChatStep(2);
    fetchEligibleUsers(role, "");
  };

  const handleDialogClose = () => {
    setOpenNewChatDialog(false);
    setNewChatStep(1);
    setSelectedRole("");
    setUserSearch("");
    setEligibleUsers([]);
    setSelectedUsers([]);
    setBulkMessage("");
  };

  const handleBackToRoleSelect = () => {
    setNewChatStep(1);
    setSelectedRole("");
    setUserSearch("");
    setEligibleUsers([]);
    setSelectedUsers([]);
    setBulkMessage("");
  };

  const handleSelectAll = () => {
    if (selectedUsers.length === eligibleUsers.length) {
      // Deselect all
      setSelectedUsers([]);
    } else {
      // Select all
      setSelectedUsers([...eligibleUsers]);
    }
  };

  const handleClearSelection = () => {
    setSelectedUsers([]);
    setBulkMessage("");
  };

  // Normalize role for comparison (API may return super_admin or superadmin)
  const roleMatches = (userRole, requestedRole) => {
    if (!userRole) return false;
    const u = userRole.toLowerCase().replace(/-/g, "_");
    const r = (requestedRole || "").toLowerCase().replace(/-/g, "_");
    if (u === r) return true;
    if ((u === "super_admin" || u === "superadmin") && (r === "super_admin" || r === "superadmin")) return true;
    return false;
  };

  const fetchEligibleUsers = async (role, search = "") => {
    if (!role) {
      console.warn("fetchEligibleUsers called without role parameter");
      setEligibleUsers([]);
      return;
    }

    setIsLoadingUsers(true);
    try {
      const response = await messagingApi.getEligibleUsers(search, 1, 50, role);
      console.log("role",role)
      console.log("response",response)

      if (!response.error && response.data?.users) {
        // Filter by role on frontend so we only show users for the selected role (backup if API returns wrong role)
        const filteredUsers = response.data.users.filter((user) =>
          roleMatches(user.role, role)
        );
        setEligibleUsers(filteredUsers);
      } else {
        setEligibleUsers([]);
      }
    } catch (error) {
      console.error("Error fetching eligible users:", error);
      setEligibleUsers([]);
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const handleUserToggle = (user) => {
    setSelectedUsers((prev) => {
      const isSelected = prev.some((u) => u.id === user.id);
      if (isSelected) {
        return prev.filter((u) => u.id !== user.id);
      } else {
        return [...prev, user];
      }
    });
  };

  const handleSendBulkMessage = async () => {
    if (selectedUsers.length === 0 || !bulkMessage.trim()) return;

    setIsSendingBulkMessage(true);
    const createdChats = [];

    try {
      // Create threads and send message to all selected users
      for (const user of selectedUsers) {
        try {
          // First, create or get the thread
          let threadId;
          const threadResponse = await messagingApi.createThread(user.id, user.role);
          
          if (!threadResponse.error && threadResponse.data?.thread) {
            threadId = threadResponse.data.thread.id;
            const chat = {
              id: threadId,
              name: user.name,
              icon: user.profile_image || "",
              other_user_id: user.id,
              role: user.role,
              msg: bulkMessage,
              time: formatThreadTime(new Date().toISOString()),
              unreadCount: 0,
              lastMessageAt: new Date().toISOString()
            };
            createdChats.push(chat);
            
            // Add to allChats
            setAllChats((prev) => {
              const exists = prev.find(c => c.id === chat.id);
              if (exists) {
                // Update existing chat
                return prev.map(c => c.id === chat.id ? chat : c);
              }
              return [chat, ...prev];
            });
            
            // Apply filter if active
            if (!filterRole || chat.role === filterRole) {
              setChatList((prev) => {
                const exists = prev.find(c => c.id === chat.id);
                if (exists) {
                  return prev.map(c => c.id === chat.id ? chat : c);
                }
                return [chat, ...prev];
              });
            }

            // Send the message to this thread
            await messagingApi.sendMessage(threadId, bulkMessage);
          }
        } catch (error) {
          console.error(`Error sending message to user ${user.id}:`, error);
        }
      }

      // Select the first created chat
      if (createdChats.length > 0) {
        handleChatClick(createdChats[0]);
      }

      // Refresh threads to update the list
      fetchThreads();
      handleDialogClose();
    } catch (error) {
      console.error("Error sending bulk messages:", error);
    } finally {
      setIsSendingBulkMessage(false);
    }
  };

  const handleUserSelect = async (user) => {
    // For non-worker roles, create thread immediately (single selection)
    if (selectedRole !== "worker") {
    try {
      const response = await messagingApi.createThread(user.id, user.role);
      if (!response.error && response.data?.thread) {
        const thread = response.data.thread;
        const chat = {
          id: thread.id,
          name: user.name,
          icon: user.profile_image || "",
          other_user_id: user.id,
          role: user.role,
          msg: "",
          time: formatThreadTime(new Date().toISOString()),
          unreadCount: 0,
          lastMessageAt: new Date().toISOString()
        };
        // Add to allChats
        setAllChats((prev) => {
          const exists = prev.find(c => c.id === chat.id);
          if (exists) return prev;
          return [chat, ...prev];
        });
        // Apply filter if active
        if (!filterRole || chat.role === filterRole) {
          setChatList((prev) => {
            const exists = prev.find(c => c.id === chat.id);
            if (exists) return prev;
            return [chat, ...prev];
          });
        }
        handleChatClick(chat);
        handleDialogClose();
      }
    } catch (error) {
      console.error("Error creating thread:", error);
      }
    } else {
      // For worker role, toggle selection
      handleUserToggle(user);
    }
  };

  const hasTextDraft = Boolean(newMessage.trim());
  const hasImageDraft = Boolean(uploadedImageUrl || imagePreview || selectedImageFile);
  const hasAudioDraft = Boolean(uploadedAudioUrl || audioBlob || isRecording);
  const hasAttachmentDraft = Boolean(hasImageDraft || uploadedFileUrl || selectedFile);

  return (
    <Box sx={{ height: "100%", minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
                      {fromDashboard && (
      <Box
        sx={{
          border: '1px solid #E4E6EF',
          borderRadius: 2,
          px: 2,
          backgroundColor: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          minHeight: 55,
          mb: 2,
          ml:1, mr:1
        }}
      >
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer' }}
          onClick={() => navigate(-1)}
        >
          <img src={back} style={{ width: 30, height: 30 }} />
          <CustomText
            sx={{
              ...textStyles.h1,
              fontWeight: 500,
              fontSize: '14px',
              color: '#626F86',
              textAlign: 'center'
            }}
          >
            {t('worker')} 
          </CustomText>
        </Box>
      </Box>
    )}
   
    <Box sx={{ display: "flex", flex: 1, minHeight: 0, bgcolor: "#F9FAFB", overflow: "hidden" }}>
      <Box sx={{ width: { xs: "100%", md: "380px" }, borderRight: "1px solid #E5E7EB", bgcolor: "white", display: { xs: selectedChat ? "none" : "flex", md: "flex" }, flexDirection: "column", height: "100%", overflow: "hidden" }}>
        <Box sx={{ p: 2.5, borderBottom: "1px solid #E5E7EB", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#1F2937'}}>
            {t("messages")} {totalCount > 0 && <Chip label={totalCount} size="small" clickable={false} sx={{ ml: 1, bgcolor: '#EF4444', color: 'white', fontSize: '12px', height: 22, cursor: 'default' }} />}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {totalCount > 0 && (
              <Tooltip title={t("Mark all as read")}>
                <IconButton 
                  onClick={markAllMessagesAsRead} 
                  disabled={isMarkingAllRead}
                  sx={{ bgcolor: '#10B981', color: 'white', '&:hover': { bgcolor: '#059669' }, '&:disabled': { bgcolor: '#D1D5DB' } }}
                >
                  {isMarkingAllRead ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <DoneAll />}
                </IconButton>
              </Tooltip>
            )}
            <IconButton onClick={() => setOpenNewChatDialog(true)} sx={{ bgcolor: '#667eea', color: 'white', '&:hover': { bgcolor: '#5568d3' } }}>
              <Add />
            </IconButton>
          </Box>
        </Box>
    
        <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
          {chatList.length > 0 ? (
            <>
              {chatList.map((chat) => (
                <Box key={chat.id} onClick={() => handleChatClick(chat)} sx={{ p: 2, borderBottom: "1px solid #E5E7EB", cursor: "pointer", bgcolor: selectedChat?.id === chat.id ? '#F3F4F6' : 'white', '&:hover': { bgcolor: '#F9FAFB' } }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Badge badgeContent={chat.unreadCount > 0 ? chat.unreadCount : 0} color="error" invisible={chat.unreadCount === 0}>
                      <Avatar src={chat.icon} sx={{ width: 50, height: 50 }}>{chat.name?.charAt(0)}</Avatar>
                    </Badge>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0, flex: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1F2937' }} noWrap>{chat.name}</Typography>
                          {chat.other_user_role && (
                            <Chip 
                              label={getRoleBadgeLabel(chat.other_user_role)} 
                              size="small"
                              clickable={false}
                              sx={{ 
                                backgroundColor: getRoleBadgeColor(chat.other_user_role),
                                color: getRoleBadgeTextColor(chat.other_user_role),
                                fontSize: '10px',
                                height: 20,
                                '& .MuiChip-label': { px: 0.75 },
                                cursor: 'inherit'
                              }}
                            />
                          )}
                        </Box>
                        <Typography variant="caption" sx={{ color: '#9CA3AF', fontSize: '0.75rem', flexShrink: 0, ml: 1 }}>{chat.time}</Typography>
                      </Box>
                      {chat.other_user_email && (
                        <Typography variant="caption" sx={{ color: '#888', fontSize: '11px', display: 'block', mb: 0.25 }} noWrap>
                          {chat.other_user_email}
                        </Typography>
                      )}
                      <Typography variant="body2" sx={{ color: '#6B7280', fontSize: '0.875rem' }} noWrap>{chat.msg || t("noMessages")}</Typography>
                    </Box>
                  </Box>
                </Box>
              ))}
              {/* Load More Conversations Button */}
              {hasMoreThreads && (
                <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
                  <Button
                    variant="outlined"
                    onClick={loadMoreThreads}
                    disabled={isLoadingMoreThreads}
                    startIcon={isLoadingMoreThreads ? <CircularProgress size={16} /> : null}
                    sx={{
                      textTransform: 'none',
                      fontSize: '13px',
                      fontFamily: 'Poppins, sans-serif',
                      color: '#667eea',
                      borderColor: '#667eea',
                      '&:hover': { borderColor: '#5568d3', bgcolor: '#EEF2FF' }
                    }}
                  >
                    {isLoadingMoreThreads ? t("Loading...") : t("Load More Conversations")}
                  </Button>
                </Box>
              )}
            </>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", p: 4, color: '#9CA3AF' }}>
              <Typography variant="body2" sx={{ textAlign: "center", color: '#6B7280' }}>
                {filterRole ? t("No chats found for this filter") : t("No messages yet")}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {selectedChat ? (
        <>
          <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column", bgcolor: "white", height: "100%", minHeight: 0, overflow: "hidden", width: { xs: "100%", md: "auto" } }}>
            <Box sx={{ p: 2.5, borderBottom: "1px solid #E5E7EB", display: "flex", alignItems: "center", gap: 2, bgcolor: "white", flexShrink: 0, position: "sticky", top: 0, zIndex: 2 }}>
              <IconButton sx={{ display: { md: "none" } }} onClick={() => setSelectedChat(null)}>
                <Close />
              </IconButton>
              <Avatar src={selectedChat.icon} sx={{ width: 44, height: 44, border: '2px solid #667eea' }}>{selectedChat.name?.charAt(0)}</Avatar>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1F2937' }}>
                  {selectedChat.name}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#10B981" }} />
                  <Typography variant="caption" sx={{ color: '#6B7280' }}>Online</Typography>
                </Box>
              </Box>
            </Box>

            <Box sx={{ flex: 1, p: { xs: 1.5, md: 3 }, overflowY: "auto", overflowX: "hidden", display: "flex", flexDirection: "column", gap: 2, backgroundImage: 'radial-gradient(circle at 1px 1px, #E5E7EB 1px, transparent 0)', backgroundSize: '40px 40px', minHeight: 0 }}>
              {/* Load Older Messages Button */}
              {hasMoreMessages ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                  <Button
                    variant="text"
                    onClick={loadMoreMessages}
                    disabled={isLoadingMoreMessages}
                    startIcon={isLoadingMoreMessages ? <CircularProgress size={14} /> : <KeyboardArrowUp />}
                    sx={{
                      textTransform: 'none',
                      fontSize: '12px',
                      fontFamily: 'Poppins, sans-serif',
                      color: '#667eea',
                      '&:hover': { bgcolor: 'rgba(102, 126, 234, 0.1)' }
                    }}
                  >
                    {isLoadingMoreMessages ? t("Loading...") : t("Load Older Messages")}
                  </Button>
                </Box>
              ) : messages.length > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                  <Typography variant="caption" sx={{ color: '#9CA3AF', fontSize: '11px', fontFamily: 'Poppins, sans-serif' }}>
                    {t("Beginning of conversation")}
                  </Typography>
                </Box>
              )}
              {messages.map((msg) => {
                const currentUserId = socketUserId || userId;
                const isOwn = String(msg.sender_id) === String(currentUserId);
                return (
                  <Box key={msg.id || msg.tempId} sx={{ alignSelf: isOwn ? "flex-end" : "flex-start", maxWidth: { xs: "88%", md: "70%" }, display: "flex", flexDirection: "column", alignItems: isOwn ? "flex-end" : "flex-start" }}>
                    <Box sx={{ p: 2, borderRadius: isOwn ? "20px 20px 4px 20px" : "20px 20px 20px 4px", background: isOwn ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" : "white", color: isOwn ? "white" : "#1F2937", boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)', maxWidth: '100%' }}>
                      {msg.message_type === "image" ? (
                        <Box>
                          <img
                            crossOrigin="anonymous"
                            src={(() => {
                              if (msg.file_url) {
                                if (msg.file_url.startsWith('http://') || msg.file_url.startsWith('https://') || msg.file_url.startsWith('data:')) {
                                  return msg.file_url;
                                }
                                return `${URL}${msg.file_url}`;
                              }
                              return '';
                            })()}
                            alt={msg.file_name || "Image"}
                            style={{ 
                              maxWidth: "250px", 
                              borderRadius: 12, 
                              cursor: "pointer",
                              display: "block"
                            }}
                          onClick={() => {
                              const imageUrl = (() => {
                                if (msg.file_url) {
                                  if (msg.file_url.startsWith('http://') || msg.file_url.startsWith('https://') || msg.file_url.startsWith('data:')) {
                                    return msg.file_url;
                                  }
                                  return `${URL}${msg.file_url}`;
                                }
                                return '';
                              })();
                              
                              if (imageUrl) {
                                window.open(imageUrl, '_blank');
                              }
                            }}
                          />
                          {msg.content &&
                            msg.content !== "File shared" &&
                            msg.content !== "Image shared" &&
                            msg.content !== "Image" && (
                            <Typography sx={{ fontSize: "13px", mt: 1, color: isOwn ? "white" : "#1F2937" }}>
                              {msg.content}
                            </Typography>
                          )}
                        </Box>
                      ) : (() => {
                        const isAudioMessage = isAudioMessagePayload(msg);

                        if (isAudioMessage) {
                          const audioFileUrl = msg.file_url?.startsWith('http://') || msg.file_url?.startsWith('https://')
                            ? msg.file_url
                            : `${URL}${msg.file_url}`;
                          
                          const progress = audioProgressById[msg.id] || { current: 0, duration: 0 };
                          const percent = progress.duration > 0 ? (progress.current / progress.duration) * 100 : 0;

                          return (
                            <Box sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.25,
                              p: 1.25,
                              backgroundColor: isOwn ? "rgba(255, 255, 255, 0.1)" : "#f5f5f5",
                              borderRadius: "14px",
                              minWidth: { xs: "0", md: "240px" },
                              width: { xs: "100%", md: "auto" },
                              maxWidth: "360px"
                            }}>
                              {/* Play/Pause Button */}
                              <IconButton
                                onClick={() => toggleAudioPlayback(msg.id, audioFileUrl)}
                                sx={{
                                  width: 38,
                                  height: 38,
                                  backgroundColor: playingAudioId === msg.id ? (isOwn ? "#005bb0" : "#005bb0") : (isOwn ? "rgba(255, 255, 255, 0.2)" : "#006EC2"),
                                  color: "white",
                                  borderRadius: "50%",
                                  '&:hover': { backgroundColor: isOwn ? "rgba(255, 255, 255, 0.3)" : "#005bb0" }
                                }}
                              >
                                {playingAudioId === msg.id ? <Pause /> : <PlayArrow />}
                              </IconButton>
                              
                              {/* Progress Bar */}
                              <Box sx={{ flex: 1, display: "flex", alignItems: "center", gap: 1 }}>
                                <Box sx={{ 
                                  position: "relative", 
                                  flex: 1, 
                                  height: 6, 
                                  borderRadius: 999, 
                                  backgroundColor: isOwn ? "rgba(255, 255, 255, 0.3)" : "#E1E4E8" 
                                }}>
                                  <Box sx={{ 
                                    position: "absolute", 
                                    top: 0, 
                                    left: 0, 
                                    height: 6, 
                                    width: `${percent}%`, 
                                    backgroundColor: isOwn ? "white" : "#006EC2", 
                                    borderRadius: 999 
                                  }} />
                                </Box>
                                
                                {/* Time Display */}
                                <Typography sx={{ fontSize: "11px", minWidth: 68, color: isOwn ? "white" : "#6B7280" }}>
                                  {formatDuration(progress.current)} / {formatDuration(progress.duration)}
                                </Typography>
                                
                                {/* Download Button */}
                                <IconButton
                                  component="a"
                                  href={audioFileUrl}
                                  download
                                  sx={{ width: 28, height: 28, color: isOwn ? "white" : "#6B7280" }}
                                >
                                  <Download />
                                </IconButton>
                              </Box>
                            </Box>
                          );
                        }

                        // Regular file/document message
                        if (msg.message_type === "file" || msg.message_type === "pdf" || msg.message_type === "document" || msg.message_type === "system") {
                          return (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, cursor: msg.file_url ? 'pointer' : 'default' }} onClick={() => {
                          if (msg.file_url) {
                            const url = msg.file_url?.startsWith('http') ? msg.file_url : `${URL}${msg.file_url}`;
                            window.open(url, '_blank');
                          }
                        }}>
                              <Description sx={{ color: isOwn ? "white" : "#6B7280" }} />
                              <Typography variant="body2" sx={{ textDecoration: msg.file_url ? 'underline' : 'none', color: isOwn ? "white" : "#1F2937" }}>
                            {msg.file_name || msg.content || "Document"}
                          </Typography>
                        </Box>
                          );
                        }

                        // Text message
                        return (
                        <Typography variant="body2" sx={{ lineHeight: 1.6 }}>{msg.content}</Typography>
                        );
                      })()}
                    </Box>
                    <Typography variant="caption" sx={{ mt: 0.5, color: '#9CA3AF', fontSize: '0.75rem' }}>
                      {formatThreadTime(msg.created_at)}
                    </Typography>
                  </Box>
                );
              })}
              <div ref={messagesEndRef} />
            </Box>

            <Box sx={{ p: 2.5, bgcolor: "white", borderTop: "1px solid #E5E7EB", boxShadow: '0 -1px 3px rgba(0, 0, 0, 0.05)', flexShrink: 0, position: "sticky", bottom: 0, zIndex: 2 }}>
              {(imagePreview || selectedFile || uploadedAudioUrl || audioBlob) && (
                <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, bgcolor: '#F3F4F6', borderRadius: 2, border: '1px solid #E5E7EB' }}>
                  {imagePreview ? (
                    <img src={imagePreview} alt="preview" style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 8 }} />
                  ) : uploadedAudioUrl || audioBlob ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, bgcolor: '#E5E7EB', borderRadius: 1 }}>
                      <Mic sx={{ color: '#667eea' }} />
                      <Typography variant="body2" sx={{ color: '#6B7280' }}>
                        Audio recorded
                      </Typography>
                    </Box>
                  ) : (
                    <Description sx={{ color: '#9CA3AF' }} />
                  )}
                  <Typography variant="body2" noWrap sx={{ flexGrow: 1, color: '#6B7280' }}>
                    {selectedImageFile?.name || selectedFile?.name || (uploadedAudioUrl || audioBlob ? "Audio.webm" : "")}
                  </Typography>
                  <IconButton size="small" onClick={() => { clearUploadedImage(); clearUploadedFile(); clearUploadedAudio(); }} sx={{ color: '#EF4444' }}>
                    <Close fontSize="small" />
                  </IconButton>
                </Box>
              )}

              {isRecording && (
                <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2, p: 1.5, bgcolor: '#FEE2E2', borderRadius: 2, border: '1px solid #FCA5A5' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box 
                      sx={{ 
                        width: 12, 
                        height: 12, 
                        borderRadius: '50%', 
                        bgcolor: '#EF4444',
                        '@keyframes pulse': {
                          '0%, 100%': { opacity: 1 },
                          '50%': { opacity: 0.5 }
                        },
                        animation: 'pulse 1.5s ease-in-out infinite'
                      }} 
                    />
                    <Typography variant="body2" sx={{ color: '#991B1B', fontWeight: 500 }}>
                      Recording: {formatDuration(recordingDuration)}
                    </Typography>
                  </Box>
                  <IconButton onClick={stopRecording} sx={{ color: '#EF4444', '&:hover': { bgcolor: '#FEE2E2' } }}>
                    <Stop />
                  </IconButton>
                </Box>
              )}

              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <input type="file" accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx" style={{ display: "none" }} id="file-upload" onChange={handleFileSelect} />
                <label htmlFor="file-upload">
                  <IconButton component="span" disabled={isRecording || isUploadingAudio || hasTextDraft || hasAudioDraft} sx={{ color: '#667eea', '&:hover': { backgroundColor: '#EEF2FF' }, '&:disabled': { color: '#9CA3AF' } }}>
                    <AttachFile />
                  </IconButton>
                </label>

                {isRecording ? (
                  <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, p: 1.5, bgcolor: '#FEE2E2', borderRadius: 2 }}>
                    <Typography variant="body2" sx={{ color: '#991B1B', fontWeight: 500 }}>
                      {formatDuration(recordingDuration)}
                    </Typography>
                  </Box>
                ) : (
                  <InputBase 
                    fullWidth 
                    placeholder={t("typeMessage")} 
                    value={newMessage} 
                    onChange={(e) => setNewMessage(e.target.value)} 
                    onKeyPress={(e) => e.key === "Enter" && !e.shiftKey && handleSendMessage()} 
                    disabled={isUploadingAudio || hasAttachmentDraft || hasAudioDraft}
                    sx={{ 
                      bgcolor: "#F3F4F6", 
                      borderRadius: 3, 
                      px: 2.5, 
                      py: 1.5, 
                      fontSize: '0.95rem', 
                      border: '1px solid #E5E7EB', 
                      '&:focus-within': { borderColor: '#667eea', backgroundColor: 'white' }, 
                      transition: 'all 0.2s ease',
                      '& input:disabled': { color: '#9CA3AF' }
                    }} 
                  />
                )}

                {!isRecording ? (
                  <IconButton 
                    onClick={startRecording} 
                    disabled={isUploadingAudio || isUploadingImage || isUploadingFile || hasTextDraft || hasAttachmentDraft} 
                    sx={{ 
                      color: '#EF4444', 
                      '&:hover': { backgroundColor: '#FEE2E2' },
                      '&:disabled': { color: '#9CA3AF' }
                    }}
                  >
                    <Mic />
                  </IconButton>
                ) : null}

                <IconButton 
                  onClick={handleSendMessage} 
                  disabled={
                    (!newMessage.trim() && !uploadedImageUrl && !uploadedFileUrl && !uploadedAudioUrl) || 
                    isUploadingImage || 
                    isUploadingFile || 
                    isUploadingAudio ||
                    isRecording
                  } 
                  sx={{ 
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
                    color: 'white', 
                    width: 48, 
                    height: 48, 
                    '&:hover': { transform: 'scale(1.05)', boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)' }, 
                    '&:disabled': { background: '#E5E7EB', color: '#9CA3AF' }, 
                    transition: 'all 0.2s ease' 
                  }}
                >
                  {(isUploadingImage || isUploadingFile || isUploadingAudio) ? (
                    <CircularProgress size={24} sx={{ color: 'white' }} />
                  ) : (
                    <Send />
                  )}
                </IconButton>
              </Box>
            </Box>
          </Box>
        </>
      ) : (
        <Box sx={{ display: { xs: "none", md: "flex" }, flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", color: "#9CA3AF", flexGrow: 1 }}>
          <img src={chatimage} alt="Select chat" style={{ width: "240px", marginBottom: "24px", opacity: 0.7 }} />
          <Typography variant="h6" sx={{ color: '#6B7280', fontWeight: 500 }}>{t("Select a conversation to start chatting")}</Typography>
        </Box>
      )}

      <Dialog open={openNewChatDialog} onClose={handleDialogClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ color: '#6B7280', fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '18px', position: "relative" }}>
          {newChatStep === 1 ? t("Start New Chat") : t("Select User")}
          <IconButton
    onClick={handleDialogClose}
    sx={{
      position: "absolute",
      right: 8,
      top: 8,
      color: "black",
    }}
  >
    <Close />
  </IconButton>
          {newChatStep === 2 && (
            <IconButton onClick={handleBackToRoleSelect} sx={{ position: 'absolute', right: 8, top: 8, color: 'black' }}>
              <Close />
            </IconButton>
          )}
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          {newChatStep === 1 ? (
            <List>
              <ListItemButton onClick={() => handleRoleSelect("superadmin")} sx={{ borderRadius: 2, mb: 1, '&:hover': { backgroundColor: '#F3F4F6' } }}>
                <ListItemAvatar>
                  {/* <Avatar sx={{ bgcolor: '#667eea', width: 48, height: 48 }}>S</Avatar> */}
                </ListItemAvatar>
                <ListItemText primary={<Typography sx={{ fontWeight: 500, fontFamily: 'Poppins, sans-serif', fontSize: "16px" }}>{t("Chat with Super Admin")}</Typography>} secondary={<Typography variant="body2" sx={{ color: '#6B7280', fontFamily: 'Poppins, sans-serif', fontSize: "14px" }}>{t("Contact support or administration")}</Typography>} />
              </ListItemButton>
              <Divider />
              <ListItemButton onClick={() => handleRoleSelect("company_admin")} sx={{ borderRadius: 2, my: 1, '&:hover': { backgroundColor: '#F3F4F6' } }}>
                <ListItemAvatar>
                  {/* <Avatar sx={{ bgcolor: '#F59E0B', width: 48, height: 48 }}>C</Avatar> */}
                </ListItemAvatar>
                <ListItemText primary={<Typography sx={{ fontWeight: 500, fontFamily: 'Poppins, sans-serif', fontSize: "16px" }}>{t("Chat with Company Admin")}</Typography>} secondary={<Typography variant="body2" sx={{ color: '#6B7280', fontFamily: 'Poppins, sans-serif', fontSize: "14px" }}>{t("Contact your company admin")}</Typography>} />
              </ListItemButton>
              <Divider />
              <ListItemButton onClick={() => handleRoleSelect("worker")} sx={{ borderRadius: 2, mt: 1, '&:hover': { backgroundColor: '#F3F4F6' } }}>
                <ListItemAvatar>
                  {/* <Avatar sx={{ bgcolor: '#10B981', width: 48, height: 48 }}>W</Avatar> */}
                </ListItemAvatar>
                <ListItemText primary={<Typography sx={{ fontWeight: 500, fontFamily: 'Poppins, sans-serif', fontSize: "16px" }}>{t("Chat with Other Workers")}</Typography>} secondary={<Typography variant="body2" sx={{ color: '#6B7280', fontFamily: 'Poppins, sans-serif', fontSize: "14px" }}>{t("Message colleagues in your company")}</Typography>} />
              </ListItemButton>
            </List>
          ) : (
            <Box>
              {!selectedRole ? (
                <Typography align="center" sx={{ py: 3, color: '#9CA3AF' }}>
                  {t("Please select a role first")}
                </Typography>
              ) : (
                <>
                  <Typography sx={{ fontSize: "14px", fontWeight: 500, color: '#374151', mb: 1, fontFamily: 'Poppins, sans-serif' }}>
                    {t("Search users...")}
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder={t("Search users...")}
                    variant="outlined"
                    size="small"
                    value={userSearch}
                    onChange={(e) => {
                      const searchValue = e.target.value;
                      setUserSearch(searchValue);
                      // Always use selectedRole when fetching
                      if (selectedRole) {
                        fetchEligibleUsers(selectedRole, searchValue);
                      }
                    }}
                    InputProps={{ startAdornment: <Search sx={{ color: '#9CA3AF', mr: 1 }} /> }}
                    sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: '14px' }, '& .MuiInputBase-input': { fontSize: '14px', fontFamily: 'Poppins, sans-serif' } }}
                  />
                  {isLoadingUsers ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    <>
                      {selectedRole === "worker" && eligibleUsers.length > 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Button
                            size="small"
                            onClick={handleSelectAll}
                            sx={{
                              textTransform: 'none',
                              fontFamily: 'Poppins, sans-serif',
                              fontSize: '13px',
                              color: '#667eea',
                              '&:hover': { bgcolor: '#EEF2FF' }
                            }}
                          >
                            {selectedUsers.length === eligibleUsers.length ? t("Deselect All") : t("Select All")}
                          </Button>
                          {selectedUsers.length > 0 && (
                            <Button
                              size="small"
                              onClick={handleClearSelection}
                              sx={{
                                textTransform: 'none',
                                fontFamily: 'Poppins, sans-serif',
                                fontSize: '13px',
                                color: '#EF4444',
                                '&:hover': { bgcolor: '#FEE2E2' }
                              }}
                            >
                              {t("Clear")}
                            </Button>
                          )}
                        </Box>
                      )}
                      <List sx={{ maxHeight: 250, overflow: 'auto' }}>
                      {eligibleUsers.length > 0 ? (
                          eligibleUsers.map((user) => {
                            const isSelected = selectedUsers.some((u) => u.id === user.id);
                            const showCheckbox = selectedRole === "worker";
                            
                            return (
                              <ListItemButton 
                                key={user.id} 
                                onClick={() => handleUserSelect(user)} 
                                sx={{ borderRadius: 2, mb: 0.5, '&:hover': { backgroundColor: '#F3F4F6' } }}
                              >
                                {showCheckbox && (
                                  <Checkbox
                                    checked={isSelected}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleUserToggle(user);
                                    }}
                                    sx={{ mr: 1 }}
                                  />
                                )}
                                <ListItemAvatar>
                                  <Avatar src={user.profile_image} sx={{ width: 40, height: 40 }}>{user.name?.charAt(0)}</Avatar>
                                </ListItemAvatar>
                                <ListItemText 
                                  primary={<Typography sx={{ fontSize: "14px", fontWeight: 500, fontFamily: 'Poppins, sans-serif' }}>{user.name}</Typography>} 
                                  secondary={<Typography sx={{ fontSize: "12px", fontFamily: 'Poppins, sans-serif' }}>{user.email}</Typography>} 
                                />
                              </ListItemButton>
                            );
                          })
                      ) : (
                        <Typography align="center" sx={{ py: 3, color: '#9CA3AF' }}>
                          {t("No users found")}
                        </Typography>
                      )}
                    </List>
                      {selectedRole === "worker" && selectedUsers.length > 0 && (
                        <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #E5E7EB' }}>
                          <Typography sx={{ fontSize: "14px", fontWeight: 500, color: '#374151', mb: 1.5, fontFamily: 'Poppins, sans-serif' }}>
                            {selectedUsers.length} {selectedUsers.length === 1 ? t("user selected") : t("users selected")}
                          </Typography>

                          {selectedUsers.length === 1 ? (
                            /* Single User: Show Start Chat Button */
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                              <Button
                                size="small"
                                onClick={handleClearSelection}
                                sx={{
                                  textTransform: 'none',
                                  fontFamily: 'Poppins, sans-serif',
                                  fontSize: '13px',
                                  color: '#6B7280',
                                  '&:hover': { bgcolor: '#F3F4F6' }
                                }}
                              >
                                {t("Clear Selection")}
                              </Button>
                              <Button
                                variant="contained"
                                onClick={async () => {
                                  const user = selectedUsers[0];
                                  try {
                                    // Create or get existing thread
                                    const response = await messagingApi.createThread(user.id, user.role);
                                    if (response?.data?.thread) {
                                      // Fetch threads to update the list
                                      await fetchThreads();
                                      // Find and open the chat
                                      const threadId = response.data.thread.id;
                                      const chat = chatList.find(c => c.id === threadId);
                                      if (chat) {
                                        handleChatClick(chat);
                                        handleDialogClose();
                                      }
                                    }
                                  } catch (error) {
                                    console.error("Error creating thread:", error);
                                  }
                                }}
                                sx={{
                                  bgcolor: '#667eea',
                                  color: 'white',
                                  textTransform: 'none',
                                  fontFamily: 'Poppins, sans-serif',
                                  fontSize: '14px',
                                  px: 3,
                                  '&:hover': { bgcolor: '#5568d3' }
                                }}
                              >
                                {t("Start Chat")}
                              </Button>
                            </Box>
                          ) : (
                            /* Multiple Users: Show Bulk Message Input */
                            <>
                              {/* Message Input Box */}
                              <TextField
                                fullWidth
                                multiline
                                rows={3}
                                placeholder={t("Type a message to send to all selected users...")}
                                value={bulkMessage}
                                onChange={(e) => setBulkMessage(e.target.value)}
                                variant="outlined"
                                size="small"
                                sx={{
                                  mb: 2,
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    fontSize: '14px',
                                    fontFamily: 'Poppins, sans-serif'
                                  }
                                }}
                              />

                              {/* Action Buttons */}
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                                <Button
                                  size="small"
                                  onClick={handleClearSelection}
                                  sx={{
                                    textTransform: 'none',
                                    fontFamily: 'Poppins, sans-serif',
                                    fontSize: '13px',
                                    color: '#6B7280',
                                    '&:hover': { bgcolor: '#F3F4F6' }
                                  }}
                                >
                                  {t("Clear Selection")}
                                </Button>
                                <Button
                                  variant="contained"
                                  onClick={handleSendBulkMessage}
                                  disabled={isSendingBulkMessage || selectedUsers.length === 0 || !bulkMessage.trim()}
                                  sx={{
                                    bgcolor: '#667eea',
                                    color: 'white',
                                    textTransform: 'none',
                                    fontFamily: 'Poppins, sans-serif',
                                    fontSize: '14px',
                                    px: 3,
                                    '&:hover': { bgcolor: '#5568d3' },
                                    '&:disabled': { bgcolor: '#E5E7EB', color: '#9CA3AF' }
                                  }}
                                >
                                  {isSendingBulkMessage ? (
                                    <>
                                      <CircularProgress size={16} sx={{ color: 'white', mr: 1 }} />
                                      {t("Sending...")}
                                    </>
                                  ) : (
                                    t("Send Message")
                                  )}
                                </Button>
                              </Box>
                            </>
                          )}
                        </Box>
                      )}
                    </>
                  )}
                </>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
    </Box>
  );
};

export default Messages;